import { type JingleCollectionModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type JingleCollectionsSortState } from '../../lib/jingle-collections-query-model'
import {
  $jingleCollectionsTablePage,
  $jingleCollectionsTablePageSize,
  $jingleCollectionsTableSort,
  jingleCollectionsTablePageChanged,
  jingleCollectionsTablePageReset,
  jingleCollectionsTablePageSizeChanged,
  jingleCollectionsTableQuery,
  jingleCollectionsTableSortChanged,
  jingleCollectionsTableSortReset,
} from '../../model/jingle-collections-table.model'
import { useJingleCollectionsTableColumns } from './useJingleCollectionsTableColumns'

interface JingleCollectionsTableProps {
  onEdit: (jingleCollectionId: string) => void
}
export const JingleCollectionsTable = ({
  onEdit,
}: JingleCollectionsTableProps) => {
  const { data, pending } = useUnit(jingleCollectionsTableQuery)

  const sort = useUnit($jingleCollectionsTableSort)
  const changeSort = useUnit(jingleCollectionsTableSortChanged)
  const resetSort = useUnit(jingleCollectionsTableSortReset)

  const page = useUnit($jingleCollectionsTablePage)
  const changePage = useUnit(jingleCollectionsTablePageChanged)
  const resetPage = useUnit(jingleCollectionsTablePageReset)
  const pageSize = useUnit($jingleCollectionsTablePageSize)
  const changePageSize = useUnit(jingleCollectionsTablePageSizeChanged)

  const tableColumns: TableProps<JingleCollectionModel>['columns'] =
    useJingleCollectionsTableColumns({ onEdit, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<JingleCollectionModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as JingleCollectionsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Подборок на странице' },
        }}
      />
    </TableWrapper>
  )
}
